import React, { useState } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const NavigationTab = () => {
  const { t } = useTranslation();
  const { config, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const navLinksPath = 'siteConfig.nav.links';
  const isNavConfigurable = isFieldConfigurable(navLinksPath);
  const isNavFixed = isFieldFixed(navLinksPath);

  // Sin declaración alguna en el schema del template (ni configurable ni fija):
  // no hay nada que mostrar.
  if (!isNavConfigurable && !isNavFixed) {
    return null;
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!isNavConfigurable || isNavFixed) {
        SnackbarUtils.warning(t('field_not_configurable') || 'Este campo no es configurable');
        return;
      }

      // Guardar el array completo de links
      const updates = {
        [navLinksPath]: config.siteConfig.nav.links
      };

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      
      // Recargar configuración
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving navigation:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreToTemplateDefault = async () => {
    try {
      setRestoring(true);
      await SitesService.resetField(navLinksPath);
      await loadConfig();
      SnackbarUtils.success(t('field_restored_to_default') || 'Se restauró el valor del template');
    } catch (err) {
      console.error('Error restoring navigation to template default:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setRestoring(false);
    }
  };

  if (isNavFixed) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleRestoreToTemplateDefault} disabled={restoring}>
              {restoring ? (t('restoring') || 'Restaurando...') : (t('restore_template_default') || 'Restaurar valor del template')}
            </Button>
          }
        >
          {t('field_fixed') || 'Este campo es fijo y no se puede modificar'}
        </Alert>
        {config.siteConfig.nav.links?.map((link, index) => (
          <Card key={index} sx={{ mb: 2, mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('link') || 'Enlace'} {index + 1}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('label') || 'Etiqueta'}
                    value={link.label || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('href') || 'URL'}
                    value={link.href || ''}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {!isNavConfigurable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      )}
      {isNavConfigurable && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleAddArrayItem('siteConfig.nav.links', { label: '', href: '' })}
              variant="outlined"
            >
              {t('add_link') || 'Agregar Enlace'}
            </Button>
          </Box>
          {config.siteConfig.nav.links?.map((link, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1">{t('link') || 'Enlace'} {index + 1}</Typography>
                  <IconButton
                    onClick={() => handleRemoveArrayItem('siteConfig.nav.links', index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('label') || 'Etiqueta'}
                      value={link.label || ''}
                      onChange={(e) => handleArrayChange('siteConfig.nav.links', index, 'label', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('href') || 'URL'}
                      value={link.href || ''}
                      onChange={(e) => handleArrayChange('siteConfig.nav.links', index, 'href', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default NavigationTab;
